import React, { useState } from "react";
import { Settings, User, Lock, Bell, Save, Loader2, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

/**
 * AccountSettingsTab — customer self-service for profile, password and
 * notification preferences. Splits into three independent cards so each
 * submit is small + fast. AuthContext is refreshed after a successful
 * profile update so the rest of the app sees the new name/phone right
 * away (no logout/login dance required).
 */
export default function AccountSettingsTab() {
  const { user, refreshUser } = useAuth();

  return (
    <div className="space-y-5" data-testid="account-settings-tab">
      <div>
        <h3 className="font-display text-2xl text-apex-cream font-bold flex items-center gap-2">
          <Settings className="w-6 h-6 text-apex-gold" />
          Nastavení účtu
        </h3>
        <p className="text-apex-cream/65 text-sm mt-2">
          Upravte své osobní údaje, heslo nebo zda chcete dostávat marketingové
          e-maily. E-mail nelze měnit — pro změnu nás kontaktujte na <a className="text-apex-gold hover:underline" href="mailto:apexdetailingprague@gmail.com">apexdetailingprague@gmail.com</a>.
        </p>
      </div>

      <ProfileCard user={user} onSaved={refreshUser} />
      <PasswordCard />
      <PreferencesCard user={user} onSaved={refreshUser} />
    </div>
  );
}

function Section({ icon: Icon, title, children, testId }) {
  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid={testId}>
      <h4 className="font-display text-lg text-apex-cream font-semibold flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-apex-gold" />
        {title}
      </h4>
      {children}
    </div>
  );
}

function FlashRow({ ok, err }) {
  if (!ok && !err) return null;
  return (
    <div
      className={`text-xs rounded-lg px-3 py-2 inline-flex items-center gap-1.5 ${
        ok ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-300"
           : "bg-red-500/10 border border-red-500/30 text-red-300"
      }`}
    >
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
      {ok || err}
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="text-xs text-apex-cream/65 uppercase tracking-wider block mb-1.5">{label}</span>
      <input
        {...props}
        className="w-full px-3 py-2.5 rounded-lg bg-apex-bg border border-apex-line text-apex-cream focus:outline-none focus:border-apex-gold/60 disabled:opacity-50"
      />
    </label>
  );
}

function ProfileCard({ user, onSaved }) {
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [address, setAddress] = useState(user?.address || "");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setOk(""); setErr("");
    try {
      await api.put("/auth/me", { firstName, lastName, phone, address });
      setOk("Profil uložen.");
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Uložení selhalo.");
    } finally { setBusy(false); }
  };

  return (
    <Section icon={User} title="Osobní údaje" testId="account-profile-card">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Jméno" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="profile-firstname" />
        <Field label="Příjmení" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="profile-lastname" />
        <Field label="Telefon" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="profile-phone" placeholder="+420 ..." />
        <Field label="E-mail (nelze měnit)" value={user?.email || ""} disabled data-testid="profile-email-readonly" />
        <div className="sm:col-span-2">
          <Field label="Adresa (volitelné)" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="profile-address" placeholder="Ulice, město, PSČ" />
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          disabled={busy}
          onClick={save}
          className="bg-apex-gold text-apex-bg font-semibold px-5 py-2 rounded-xl inline-flex items-center gap-2 hover:bg-apex-gold-soft disabled:opacity-50"
          data-testid="profile-save-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Uložit změny
        </button>
        <FlashRow ok={ok} err={err} />
      </div>
    </Section>
  );
}

function PasswordCard() {
  const [currentPassword, setCurrent] = useState("");
  const [newPassword, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");
  // Visibility per field — every password input has its own toggle so the
  // user can verify one without exposing the others.
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const save = async () => {
    setOk(""); setErr("");
    if (newPassword.length < 6) { setErr("Heslo musí mít alespoň 6 znaků."); return; }
    if (newPassword !== confirm) { setErr("Nové heslo a potvrzení se neshodují."); return; }
    setBusy(true);
    try {
      await api.post("/auth/me/change-password", { currentPassword, newPassword });
      setOk("Heslo bylo změněno.");
      setCurrent(""); setNewPwd(""); setConfirm("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Změna selhala.");
    } finally { setBusy(false); }
  };

  return (
    <Section icon={Lock} title="Změnit heslo" testId="account-password-card">
      <div className="grid sm:grid-cols-3 gap-3">
        <PasswordField label="Aktuální heslo" value={currentPassword} onChange={setCurrent} show={showCurrent} setShow={setShowCurrent} testId="pwd-current" />
        <PasswordField label="Nové heslo" value={newPassword} onChange={setNewPwd} show={showNew} setShow={setShowNew} testId="pwd-new" />
        <PasswordField label="Nové heslo znovu" value={confirm} onChange={setConfirm} show={showConfirm} setShow={setShowConfirm} testId="pwd-confirm" />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          disabled={busy || !currentPassword || !newPassword}
          onClick={save}
          className="bg-apex-gold text-apex-bg font-semibold px-5 py-2 rounded-xl inline-flex items-center gap-2 hover:bg-apex-gold-soft disabled:opacity-50"
          data-testid="pwd-save-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Změnit heslo
        </button>
        <FlashRow ok={ok} err={err} />
      </div>
    </Section>
  );
}

function PasswordField({ label, value, onChange, show, setShow, testId }) {
  return (
    <label className="block">
      <span className="text-xs text-apex-cream/65 uppercase tracking-wider block mb-1.5">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full pl-3 pr-10 py-2.5 rounded-lg bg-apex-bg border border-apex-line text-apex-cream focus:outline-none focus:border-apex-gold/60"
          data-testid={testId}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md inline-flex items-center justify-center text-apex-cream/55 hover:text-apex-gold transition-colors"
          aria-label={show ? "Skrýt heslo" : "Zobrazit heslo"}
          data-testid={`${testId}-toggle`}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}

function PreferencesCard({ user, onSaved }) {
  const initialPrefs = user?.preferences || {};
  const [marketing, setMarketing] = useState(initialPrefs.marketingEmails !== false);
  const [reminders, setReminders] = useState(initialPrefs.bookingReminders !== false);
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setOk(""); setErr("");
    try {
      await api.put("/auth/me/preferences", { marketingEmails: marketing, bookingReminders: reminders });
      setOk("Preference uloženy.");
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Uložení selhalo.");
    } finally { setBusy(false); }
  };

  return (
    <Section icon={Bell} title="Notifikace" testId="account-preferences-card">
      <div className="space-y-3">
        <Toggle
          checked={marketing}
          onChange={setMarketing}
          label="Marketingové e-maily"
          hint="Akce, sezónní tipy, nabídky věrnostních balíčků."
          testId="pref-marketing"
        />
        <Toggle
          checked={reminders}
          onChange={setReminders}
          label="Připomenutí termínů a plateb"
          hint="Den před vaším termínem dostanete e-mailovou připomínku. Potvrzovací e-maily o rezervaci nelze vypnout."
          testId="pref-reminders"
        />
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          disabled={busy}
          onClick={save}
          className="bg-apex-gold text-apex-bg font-semibold px-5 py-2 rounded-xl inline-flex items-center gap-2 hover:bg-apex-gold-soft disabled:opacity-50"
          data-testid="pref-save-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Uložit preference
        </button>
        <FlashRow ok={ok} err={err} />
      </div>
    </Section>
  );
}

function Toggle({ checked, onChange, label, hint, testId }) {
  return (
    <div
      onClick={() => onChange(!checked)}
      className="flex items-start gap-3 cursor-pointer rounded-xl bg-apex-bg/60 border border-apex-line p-3.5 hover:border-apex-gold/40 transition"
      data-testid={testId}
      role="switch"
      aria-checked={checked}
    >
      <div
        className={`shrink-0 w-11 h-6 rounded-full p-0.5 mt-0.5 transition-colors ${checked ? "bg-apex-gold" : "bg-apex-card border border-apex-line"}`}
      >
        <div
          className={`w-5 h-5 rounded-full bg-apex-bg shadow-sm transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-apex-cream font-semibold text-sm">{label}</div>
        <div className="text-apex-cream/60 text-xs mt-0.5">{hint}</div>
      </div>
    </div>
  );
}
